'use client';
import React from 'react';
import { Icon as TWIcon } from '@osmosis/tailwind-ui/components/Icon';
import { faPlay } from '@fortawesome/free-solid-svg-icons/faPlay';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { ResourcesBlockArgsExtended } from '@osmosis/shared/types/pages/answerPages';
import Divider from '../Divider';
import VideoList from '../VideoList';
import {
  borderClasses800,
  borderColor300,
} from '@osmosis/tailwind-ui/styles/border';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { bg2, text4 } from '@osmosis/tailwind-ui/styles';
import { useIntl } from 'react-intl';

type Props = {
  resourcesObj: ResourcesBlockArgsExtended;
};

/**
 * This is the sidebar component for the Answer page.
 * It will display a list of related videos and concepts.
 * @returns The sidebar component for the Answer page.
 */
const AnswersSidebar = ({ resourcesObj }: Props) => {
  const { formatMessage } = useIntl();
  const headerText = formatMessage({
    id: 'answers.sidebar.header',
    defaultMessage: 'Related topics',
  });
  const { recommendedConcepts: videos } = resourcesObj;

  if (!videos || videos.length === 0) {
    return null;
  }

  return (
    <Box
      cn={cn('border rounded-lg w-full', borderColor300)}
      data-testid="answers-page-sidebar"
    >
      <Box cn="p-4 pb-2">
        <Flex cn="items-center mb-2">
          <Icon />
          <span className={cn('font-bold text-lg', text4)}>{headerText}</span>
        </Flex>
        <Divider className={`absolute left-0 right-0 -mr-1 ${bg2} h-px`} />
      </Box>
      <Box className="p-2">
        <VideoList testId="answer-page-video-list" videos={videos || []} />
      </Box>
    </Box>
  );
};

/**
 * This is the Icon component for the header of related video and concepts.
 * @returns  The Icon component.
 */
export const Icon = () => (
  <Flex
    cn={cn(
      'flex items-center justify-center w-6 h-5 rounded-sm mr-2',
      borderClasses800,
    )}
  >
    <TWIcon
      icon={faPlay}
      cn={cn('text-xs', text4)}
      data-testid="answer-sidebar-video-header-icon"
    />
  </Flex>
);

export default AnswersSidebar;
